import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMealTypes } from '../store/mealTypeSlice';
import { fetchMenus } from '../store/menuSlice';
import {
  fetchCustomerMenus,
  createCustomerMenu,
  updateCustomerMenu,
  deleteCustomerMenu
} from '../store/customerMenuSlice';
import { Dialog } from '@headlessui/react';
import dayjs from 'dayjs';

const CustomerMenuScreen = () => {
  const dispatch = useDispatch();

  const { mealTypes, isLoading: mealTypesLoading } = useSelector((state) => state.mealType);
  const { menus, isLoading: menusLoading } = useSelector((state) => state.menu);
  const { customerMenus, isLoading: customerMenusLoading } = useSelector((state) => state.customerMenu);

  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [activeMealTypeId, setActiveMealTypeId] = useState(null);
  const [selectedMenuType, setSelectedMenuType] = useState('');
  const [modalMenus, setModalMenus] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingMenuSelections, setPendingMenuSelections] = useState([]);

  const isLoading = mealTypesLoading || menusLoading || customerMenusLoading;

  useEffect(() => {
    dispatch(fetchMealTypes());
    dispatch(fetchMenus());
    dispatch(fetchCustomerMenus());
  }, [dispatch]);

  const openModal = (mealtypeId, menutype) => {
    setActiveMealTypeId(mealtypeId);
    setSelectedMenuType(menutype);
    const filtered = menus.filter((m) => m.mealtypeId === mealtypeId && m.menutype === menutype);
    setModalMenus(filtered);
    setIsModalOpen(true);
  };

  const handleRemoveMenu = (menu) => {
    if (menu.id) {
      dispatch(deleteCustomerMenu(menu.id));
    } else {
      setPendingMenuSelections((prev) =>
        prev.filter(
          (item) =>
            item.menuId !== menu.menuId ||
            item.menutype !== menu.menutype ||
            item.mealtypeId !== menu.mealtypeId ||
            item.menuDate !== menu.menuDate
        )
      );
    }
  };

  const handleSelectMenu = (menu) => {
    const alreadyExists = [...customerMenus, ...pendingMenuSelections].find(
      (cm) =>
        cm.menuId === menu.id &&
        dayjs(cm.menuDate).format('YYYY-MM-DD') === selectedDate &&
        cm.mealtypeId === menu.mealtypeId &&
        cm.menutype === menu.menutype
    );

    if (alreadyExists) {
      setIsModalOpen(false);
      return;
    }

    setPendingMenuSelections((prev) => [
      ...prev,
      {
        menuId: menu.id,
        price: menu.price,
        menutype: menu.menutype,
        mealtypeId: menu.mealtypeId,
        menuDate: selectedDate,
        menu,
        status: 'Pending',
      },
    ]);

    setIsModalOpen(false);
  };

  const postMenusWithStatus = (status) => {
    const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');

    if (status === 'Published') {
      customerMenus.forEach((menu) => {
        const menuDate = dayjs(menu.menuDate).format('YYYY-MM-DD');
        if (menu.status === 'Active' && menuDate === formattedDate) {
          dispatch(
            updateCustomerMenu({
              id: menu.id,
              data: { status: 'Published', menuDate: formattedDate },
            })
          );
        }
      });
    }

    pendingMenuSelections.forEach((entry) => {
      const existing = customerMenus.find(
        (cm) =>
          cm.menuId === entry.menuId &&
          dayjs(cm.menuDate).format('YYYY-MM-DD') === formattedDate &&
          cm.mealtypeId === entry.mealtypeId &&
          cm.menutype === entry.menutype
      );

      if (!existing) {
        dispatch(
          createCustomerMenu({
            menuId: entry.menuId,
            price: entry.price,
            menutype: entry.menutype,
            mealtypeId: entry.mealtypeId,
            status,
            menuDate: formattedDate,
          })
        );
      }
    });

    setPendingMenuSelections([]);
  };

  const getSelectedMenus = useCallback(
    (mealtypeId, menutype) => {
      const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
      return [...customerMenus, ...pendingMenuSelections].filter(
        (cm) =>
          dayjs(cm.menuDate).format('YYYY-MM-DD') === formattedDate &&
          cm.mealtypeId === mealtypeId &&
          cm.menutype === menutype
      );
    },
    [customerMenus, pendingMenuSelections, selectedDate]
  );

  if (isLoading) {
    return <div className="p-6 text-gray-600">Loading...</div>;
  }

  if (!menus.length) {
    return <div className="p-6 text-gray-500">No menus available. Please add some first.</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold">Customer Menu Planner</h1>

      <div className="flex items-center gap-4">
        <label className="block text-sm font-medium text-gray-700">Select Date</label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border px-4 py-2 rounded"
        />
        <button
          onClick={() => postMenusWithStatus('Active')}
          className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
        >
          Save
        </button>
        <button
          onClick={() => postMenusWithStatus('Published')}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Publish
        </button>
      </div>

      {mealTypes.map((meal) => (
        <div key={meal.id} className="bg-white shadow p-4 rounded mt-4">
          <h2 className="text-lg font-semibold mb-2">{meal.type}</h2>
          <div className="flex gap-4 mb-4">
            {['Classic', 'Rolling'].map((type) => (
              <button
                key={type}
                onClick={() => openModal(meal.id, type)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Add {type}
              </button>
            ))}
          </div>

          {['Classic', 'Rolling'].map((type) => {
            const selected = getSelectedMenus(meal.id, type);
            return selected.length > 0 ? (
              <div key={type}>
                <h3 className="font-medium text-gray-700 mb-1">{type} Menus</h3>
                <div className="grid grid-cols-2 gap-4">
                  {selected.map((cm, idx) => (
                    <div key={idx} className="flex border rounded p-3 items-center gap-4">
                      <img
                        src={cm.menu?.menuImages?.[0]?.imageUrl || '/noimage.jpg'}
                        alt={cm.menu?.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="font-semibold">{cm.menu?.name}</p>
                        <p className="text-sm text-gray-500">{cm.menu?.description}</p>
                        <p className="text-sm text-gray-600">Price: ₹{cm.price}</p>
                        <p className="text-sm text-gray-600">Date: {cm.menuDate}</p>
                        <span className="text-xs font-medium text-green-600">{cm.status}</span>
                      </div>
                      {cm.status !== 'Published' && (
                        <button
                          className="text-red-600 text-sm hover:underline"
                          onClick={() => handleRemoveMenu(cm)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })}
        </div>
      ))}

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white p-6 rounded shadow max-w-lg w-full space-y-4">
            <Dialog.Title className="text-lg font-semibold">
              Select Menu ({selectedMenuType})
            </Dialog.Title>
            {modalMenus.length === 0 ? (
              <p>No menus found for this meal type and menu type.</p>
            ) : (
              modalMenus.map((menu) => (
                <div key={menu.id} className="border p-4 rounded flex justify-between items-center">
                  <div>
                    <p className="font-semibold">{menu.name}</p>
                    <p className="text-sm text-gray-500">{menu.description}</p>
                  </div>
                  <button
                    onClick={() => handleSelectMenu(menu)}
                    className="text-blue-600 hover:underline"
                  >
                    Select
                  </button>
                </div>
              ))
            )}
            <button
              onClick={() => setIsModalOpen(false)}
              className="text-red-600 text-sm underline"
            >
              Close
            </button>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default CustomerMenuScreen;
